import React, { useState } from 'react';
import { Card, Button, Space, Drawer, Form, Select, Switch, Row, Col, Typography, Tabs } from 'antd';
import { ApiOutlined } from '@ant-design/icons';
import type { Plugin } from '../mockData';

const { TabPane } = Tabs;

interface PluginConfigProps {
  plugins: Plugin[];
  scope: 'service' | 'route';
  serviceId: string;
  routeId?: string;
  onPluginChange?: (pluginName: string, enabled: boolean) => void;
}

const PluginConfig: React.FC<PluginConfigProps> = ({ plugins, scope, serviceId, routeId, onPluginChange }) => {
  const [form] = Form.useForm();
  const [configDrawerVisible, setConfigDrawerVisible] = useState(false);
  const [currentPlugin, setCurrentPlugin] = useState<Plugin | null>(null);

  const handlePluginConfig = (plugin: Plugin) => {
    setCurrentPlugin(plugin);
    setConfigDrawerVisible(true);
  };

  const handleConfigSubmit = () => {
    form.validateFields().then(values => {
      console.log('Plugin config values:', values);
      // Here you would typically save the plugin configuration
      setConfigDrawerVisible(false);
    });
  };

  const renderPluginConfigDrawer = () => {
    if (!currentPlugin) return null;

    return (
      <Drawer
        title={`${currentPlugin.name} 配置`}
        placement="right"
        width={600}
        onClose={() => setConfigDrawerVisible(false)}
        open={configDrawerVisible}
        extra={
          <Space>
            <Button onClick={() => setConfigDrawerVisible(false)}>取消</Button>
            <Button type="primary" onClick={handleConfigSubmit}>
              确定
            </Button>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            template: 'ip黑白名单模版',
            sync_enabled: false,
            list_type: '黑名单',
            ip_list: ['127.0.0.1']
          }}
        >
          <Typography.Title level={5}>根据过滤请求</Typography.Title>
          <Form.Item
            name="template"
            label="绑定模版"
          >
            <Select>
              <Select.Option value="ip黑白名单模版">ip黑白名单模版</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="sync_enabled"
            label="关联同步"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Typography.Title level={5} style={{ marginTop: '24px' }}>配置参数</Typography.Title>
          <Form.Item
            name="list_type"
            label="黑白名单类型"
          >
            <Select>
              <Select.Option value="黑名单">黑名单</Select.Option>
              <Select.Option value="白名单">白名单</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="ip_list"
            label="ip列表"
          >
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="请输入IP地址"
              tokenSeparators={[',']}
            />
          </Form.Item>
        </Form>
      </Drawer>
    );
  };

  const renderPluginCard = (plugin: Plugin, description: string, icon?: React.ReactNode) => (
    <Col key={plugin.id}>
      <Card 
        size="small"
        bordered
        style={{ 
          width: '200px',
          textAlign: 'center',
          marginRight: '8px',
          backgroundColor: plugin.enabled ? '#e6f7ff' : 'white'
        }}
        bodyStyle={{ 
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <div style={{ 
          borderBottom: '1px solid #f0f0f0',
          width: '100%',
          padding: '8px',
          color: 'rgba(0, 0, 0, 0.85)',
          fontWeight: 'normal',
          fontSize: '14px'
        }}>
          {plugin.name}
        </div>
        <div style={{
          padding: '16px 12px',
          color: '#00000073',
          fontSize: '12px',
          lineHeight: '1.5',
          minHeight: '100px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {icon || <ApiOutlined style={{ fontSize: '20px', marginBottom: '8px' }} />}
          <div style={{ marginBottom: '8px' }}>
            {description}
          </div>
          <Button 
            type="link" 
            size="small"
            style={{ padding: 0, height: 'auto', fontSize: '12px' }}
            onClick={() => {
              console.log('View docs for:', plugin.name);
            }}
          >
            使用说明
          </Button>
        </div>
        <div style={{
          borderTop: '1px solid #f0f0f0',
          width: '100%',
          padding: '8px',
          textAlign: 'center'
        }}>
          <Space>
            <Button 
              type={plugin.enabled ? 'primary' : 'default'}
              size="small"
              onClick={() => {
                handlePluginConfig(plugin);
                if (!plugin.enabled) {
                  onPluginChange?.(plugin.name, true);
                }
              }}
            >
              {plugin.enabled ? '修改' : '绑定'}
            </Button>
            {plugin.enabled && (
              <Button 
                type="link"
                danger
                size="small"
                onClick={() => {
                  onPluginChange?.(plugin.name, false);
                }}
              >
                解绑
              </Button>
            )}
          </Space>
        </div>
      </Card>
    </Col>
  );

  const authPlugins = plugins.filter(p => 
    p.category === 'auth'
  );

  const rateLimitingPlugins = plugins.filter(p => 
    p.category === 'rate-limiting'
  );

  const securityPlugins = plugins.filter(p => 
    p.category === 'security'
  );

  const transformationPlugins = plugins.filter(p => 
    p.category === 'transformation'
  );

  const renderAuthPlugins = () => (
    <Row gutter={[8, 8]}>
      {authPlugins.map(plugin => renderPluginCard(
        plugin,
        plugin.name === 'jwt' ? '基于JWT令牌的身份验证' :
        plugin.name === 'basic-auth' ? '基于HTTP Basic的身份验证' :
        plugin.name === 'hmac-auth' ? '基于HMAC签名的身份验证' :
        '基于Wolf RBAC的访问控制'
      ))}
    </Row>
  );

  const renderRateLimitingPlugins = () => (
    <Row gutter={[8, 8]}>
      {rateLimitingPlugins.map(plugin => renderPluginCard(
        plugin,
        plugin.name === 'percentage-rate-limit' ? '按百分比限流，例如允许50%通过' :
        '请求频率限流，例如限制每秒5个请求'
      ))}
    </Row>
  );

  const renderSecurityPlugins = () => (
    <Row gutter={[8, 8]}>
      {securityPlugins.map(plugin => renderPluginCard(
        plugin,
        plugin.name === 'ip-restriction' ? 'IP黑白名单过滤请求' :
        '跨域访问CORS'
      ))}
    </Row>
  );

  const renderTransformationPlugins = () => (
    <Row gutter={[8, 8]}>
      {transformationPlugins.map(plugin => renderPluginCard(
        plugin,
        plugin.name === 'request-transformer' ? '修改请求Header、QueryString以及Path' :
        plugin.name === 'response-transformer' ? '修改响应Header和Body' :
        plugin.name === 'jsonp' ? '跨域访问JSONP' :
        '请求Path重写'
      ))}
    </Row>
  );

  const renderAllPlugins = () => {
    const allPlugins = [...plugins].sort((a, b) => {
      if (a.enabled === b.enabled) return 0;
      return a.enabled ? -1 : 1;
    });

    return (
      <Row gutter={[8, 8]}>
        {allPlugins.map(plugin => renderPluginCard(
          plugin,
          plugin.name === 'jwt' ? '基于JWT令牌的身份验证' :
          plugin.name === 'basic-auth' ? '基于HTTP Basic的身份验证' :
          plugin.name === 'hmac-auth' ? '基于HMAC签名的身份验证' :
          plugin.name === 'wolf-rbac' ? '基于Wolf RBAC的访问控制' :
          plugin.name === 'percentage-rate-limit' ? '按百分比限流，例如允许50%通过' :
          plugin.name === 'rate-limiting' ? '请求频率限流，例如限制每秒5个请求' :
          plugin.name === 'ip-restriction' ? 'IP黑白名单过滤请求' :
          plugin.name === 'cors' ? '跨域访问CORS' :
          plugin.name === 'request-transformer' ? '修改请求Header、QueryString以及Path' :
          plugin.name === 'response-transformer' ? '修改响应Header和Body' :
          plugin.name === 'jsonp' ? '跨域访问JSONP' :
          '请求Path重写'
        ))}
      </Row>
    );
  };

  return (
    <Card title={`插件配置${scope === 'route' ? ' (路由级别)' : ''}`} style={{ marginBottom: '24px' }}>
      <Tabs>
        <TabPane tab="全部" key="all">
          {renderAllPlugins()}
        </TabPane>
        <TabPane tab="认证" key="auth">
          {renderAuthPlugins()}
        </TabPane>
        <TabPane tab="限流" key="rate-limiting">
          {renderRateLimitingPlugins()}
        </TabPane>
        <TabPane tab="安全" key="security">
          {renderSecurityPlugins()}
        </TabPane>
        <TabPane tab="数据转换" key="data-transformation">
          {renderTransformationPlugins()}
        </TabPane>
      </Tabs>
      {renderPluginConfigDrawer()}
    </Card>
  );
};

export default PluginConfig; 